/**
 * Left pane: the session list.
 *
 * The load-bearing rule of the whole surface lives here — a FLAGGED count has
 * no Approve button. It shows "Review N flags" instead, which opens the
 * step-through. Clean counts approve in one click, from the row or from the
 * bulk bar. (The server enforces the same rule in `approveProposal`; this is
 * the affordance, not the gate.)
 *
 * Row interaction uses the stretched-overlay pattern: the meta block is the
 * selection button and its `after:` pseudo-element covers the row, while the
 * checkbox and the action buttons sit above it. That keeps one row = one
 * click target without nesting interactive elements inside a <button>.
 */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardCheck, Flag, Loader2, PackageSearch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ClientOrgBadge } from '@/components/ClientOrgBadge';
import { useDateFnsLocale } from '@/lib/format-dates';
import { cn } from '@/lib/utils';
import type { StocktakeProposalSummary } from '@threadheads/shared';
import { formatDate, formatQty, formatSigned } from '../lib/format';
import { FlagChip, StocktakeStatusPill, WaitChip } from './StocktakeStatusPill';

interface StocktakeListProps {
  proposals: StocktakeProposalSummary[];
  selectedId: string | null;
  onSelect: (proposalId: string) => void;
  picked: ReadonlySet<string>;
  onTogglePick: (proposalId: string) => void;
  onClearPicks: () => void;
  onApproveSelected: () => void;
  onApproveOne: (proposal: StocktakeProposalSummary) => void;
  onReviewFlags: (proposal: StocktakeProposalSummary) => void;
  onRejectOne: (proposal: StocktakeProposalSummary) => void;
  /** True when the caller may act (client org + Admin/Coordinator). */
  canAct: (proposal: StocktakeProposalSummary) => boolean;
  counterpartyName: (proposal: StocktakeProposalSummary) => string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  emptyTitle: string;
  emptyDescription: string;
  filtersActive: boolean;
  onClearFilters: () => void;
  /**
   * A row action that has to load the count's detail before it can open its
   * confirm dialog. The clicked button holds a spinner until it resolves, so
   * the dialog can never appear over half-loaded figures.
   */
  pendingRowAction: { proposalId: string; action: 'approve' | 'reject' } | null;
  /** A bulk-approve sweep is running: both bulk entry points stand down. */
  sweeping: boolean;
}

export function StocktakeList({
  proposals,
  selectedId,
  onSelect,
  picked,
  onTogglePick,
  onClearPicks,
  onApproveSelected,
  onApproveOne,
  onReviewFlags,
  onRejectOne,
  canAct,
  counterpartyName,
  isLoading,
  isError,
  onRetry,
  emptyTitle,
  emptyDescription,
  filtersActive,
  onClearFilters,
  pendingRowAction,
  sweeping,
}: StocktakeListProps) {
  const { t } = useTranslation('inventory');
  const dateLocale = useDateFnsLocale();
  const listRef = useRef<HTMLDivElement>(null);

  // Arrow keys move focus AND selection so the detail pane follows the
  // keyboard (mirrors ClientOrdersPage's card list).
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const rows = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[data-stocktake-row]') ?? [],
    );
    if (rows.length === 0) return;
    const activeIndex = rows.indexOf(document.activeElement as HTMLButtonElement);
    const nextIndex =
      event.key === 'ArrowDown'
        ? Math.min(rows.length - 1, activeIndex + 1)
        : Math.max(0, activeIndex === -1 ? 0 : activeIndex - 1);
    const next = rows[nextIndex];
    if (!next) return;
    event.preventDefault();
    next.focus();
    next.click();
  };

  const pickedCount = picked.size;

  return (
    <div className="flex min-h-0 flex-col">
      {pickedCount > 0 ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b bg-threadheads-purple/[0.07] px-3.5 py-2">
          <span className="text-xs font-semibold tabular-nums">
            {t('stocktakes.bulk.selected', { count: pickedCount })}
          </span>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={onClearPicks}
            disabled={sweeping}
          >
            {t('stocktakes.bulk.clear')}
          </Button>
          <Button
            size="sm"
            className="h-7 gap-1.5 bg-threadheads-purple px-2.5 text-xs text-white hover:bg-threadheads-purple/90"
            onClick={onApproveSelected}
            disabled={sweeping}
          >
            {sweeping ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            {t('stocktakes.bulk.approveSelected')}
          </Button>
        </div>
      ) : null}

      <div ref={listRef} onKeyDown={handleKeyDown} className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <ul className="divide-y">
            {Array.from({ length: 5 }).map((_, index) => (
              <li key={index} className="space-y-2 px-3.5 py-3">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-6 w-24" />
              </li>
            ))}
          </ul>
        ) : isError ? (
          <div className="p-4">
            <EmptyState
              icon={PackageSearch}
              title={t('stocktakes.empty.error.title')}
              description={t('stocktakes.empty.error.description')}
              size="sm"
              action={
                <Button variant="outline" size="sm" onClick={onRetry}>
                  {t('stocktakes.empty.error.action')}
                </Button>
              }
            />
          </div>
        ) : proposals.length === 0 ? (
          <div className="p-4">
            {filtersActive ? (
              <EmptyState
                icon={PackageSearch}
                title={t('stocktakes.empty.filtered.title')}
                description={t('stocktakes.empty.filtered.description')}
                size="sm"
                action={
                  <Button variant="ghost" size="sm" onClick={onClearFilters}>
                    {t('stocktakes.empty.filtered.action')}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={ClipboardCheck}
                title={emptyTitle}
                description={emptyDescription}
                size="sm"
              />
            )}
          </div>
        ) : (
          <ul>
            {proposals.map((proposal) => {
              const selected = proposal.proposalId === selectedId;
              const actionable = proposal.status === 'pending' && canAct(proposal);
              // Two different questions, two different numbers. The CHIP says
              // "this count tripped guardrails" (flagsCount, including the
              // not-counted card). The BUTTON asks "is there anything left to
              // decide" (unresolvedFlagsCount) — a fully triaged count, and a
              // partial-but-otherwise-clean one, approve in a single click.
              const hasFlags = proposal.flagsCount > 0;
              const needsReview = proposal.unresolvedFlagsCount > 0;
              const countedOn = formatDate(
                proposal.countedAt ?? proposal.submittedAt ?? proposal.createdAt,
                dateLocale,
              );
              return (
                <li
                  key={proposal.proposalId}
                  className={cn(
                    'relative border-b transition-colors duration-200 motion-reduce:transition-none',
                    selected
                      ? 'bg-threadheads-purple/[0.08] shadow-[inset_3px_0_0_0_#7E69AB]'
                      : 'hover:bg-muted/40',
                  )}
                >
                  <div className="flex items-start gap-2.5 px-3.5 py-3">
                    {actionable ? (
                      <Checkbox
                        className="relative z-10 mt-0.5"
                        checked={picked.has(proposal.proposalId)}
                        onCheckedChange={() => onTogglePick(proposal.proposalId)}
                        aria-label={t('stocktakes.row.selectAria', {
                          location: proposal.locationName ?? '',
                        })}
                      />
                    ) : (
                      <span className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    )}
                    <div className="min-w-0 flex-1">
                      <button
                        type="button"
                        data-stocktake-row
                        onClick={() => onSelect(proposal.proposalId)}
                        aria-current={selected ? 'true' : undefined}
                        className="block w-full rounded-sm text-left after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <span className="flex flex-wrap items-center gap-1.5">
                          <StocktakeStatusPill status={proposal.status} />
                          <strong className="text-[13px] font-semibold">
                            {proposal.locationName ?? t('stocktakes.row.unknownLocation')}
                          </strong>
                          {hasFlags && proposal.status === 'pending' ? (
                            <FlagChip>
                              <Flag className="h-3 w-3" aria-hidden="true" />
                              <span className="tabular-nums">{proposal.flagsCount}</span>
                            </FlagChip>
                          ) : null}
                        </span>
                        <span className="mt-1 block text-[11.5px] tabular-nums text-muted-foreground">
                          {/* `count` drives plural selection; `skus` carries
                              the locale-separated figure that actually prints. */}
                          {proposal.countedByName
                            ? t('stocktakes.row.metaBy', {
                                count: proposal.skuCount,
                                skus: formatQty(proposal.skuCount),
                                net: formatSigned(proposal.netChange),
                                date: countedOn,
                                name: proposal.countedByName,
                              })
                            : t('stocktakes.row.meta', {
                                count: proposal.skuCount,
                                skus: formatQty(proposal.skuCount),
                                net: formatSigned(proposal.netChange),
                                date: countedOn,
                              })}
                        </span>
                      </button>
                      {/* No z-index: the badge is not interactive, so lifting
                          it above the row overlay only carved a dead strip
                          out of the row's click target. */}
                      <div className="mt-1.5">
                        <ClientOrgBadge
                          orgName={counterpartyName(proposal)}
                          srLabel={t('stocktakes.badge.countAria', {
                            org: counterpartyName(proposal),
                          })}
                        />
                      </div>
                      {actionable ? (
                        <div className="relative z-10 mt-2 flex flex-wrap gap-1.5">
                          {needsReview ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 border-amber-300 bg-amber-100 px-2 text-[11px] text-amber-800 hover:bg-amber-200 hover:text-amber-900"
                              onClick={() => onReviewFlags(proposal)}
                              disabled={sweeping}
                            >
                              {t('stocktakes.action.reviewFlags', {
                                count: proposal.unresolvedFlagsCount,
                              })}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="h-6 gap-1 bg-threadheads-purple px-2 text-[11px] text-white hover:bg-threadheads-purple/90"
                              onClick={() => onApproveOne(proposal)}
                              disabled={sweeping || pendingRowAction != null}
                            >
                              {pendingRowAction?.proposalId === proposal.proposalId &&
                              pendingRowAction.action === 'approve' ? (
                                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                              ) : null}
                              {t('stocktakes.action.approve')}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 gap-1 border-orange-300 px-2 text-[11px] text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                            onClick={() => onRejectOne(proposal)}
                            disabled={sweeping || pendingRowAction != null}
                          >
                            {pendingRowAction?.proposalId === proposal.proposalId &&
                            pendingRowAction.action === 'reject' ? (
                              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                            ) : null}
                            {t('stocktakes.action.reject')}
                          </Button>
                        </div>
                      ) : proposal.status === 'waiting_on_3pl' ? (
                        <div className="relative z-10 mt-2">
                          <WaitChip>{t('stocktakes.row.awaitingReply')}</WaitChip>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
